import type { CSSProperties } from 'react';
import { Button, ButtonGroup } from 'react-bootstrap';
import { TbCheck, TbLetterX } from 'react-icons/tb';

export type ToggleButtonProps = {
  isOff: boolean;
  onClick: () => void;
  enabled?: boolean;
} & {};

const ToggleButton = ({ isOff, onClick, enabled = true }: ToggleButtonProps): JSX.Element => {
  const baseButtonStyles: CSSProperties = {
    borderWidth: 0,
    borderStyle: 'none',
    transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
    cursor: enabled ? 'pointer' : 'default',
    flex: '1 1 0',
    borderRadius: 0,
  };

  const activeSuccess: CSSProperties = {
    ...baseButtonStyles,
    backgroundColor: 'var(--color-success-button)',
    borderColor: 'var(--color-status-success)',
    color: 'var(--color-primary-highlight-foreground)',
    boxShadow: '0 0 10px rgba(var(--color-status-success) 0.55)',
  };
  const inactiveSuccess: CSSProperties = {
    ...baseButtonStyles,
    backgroundColor: 'var(--color-success-button)',
    borderColor: 'var(--color-status-success)',
    color: 'var(--color-primary-shadow)',
    filter: 'brightness(0.65)',
  };

  const activeDanger: CSSProperties = {
    ...baseButtonStyles,
    backgroundColor: 'var(--color-danger-button)',
    borderColor: 'var(--color-status-danger)',
    color: 'var(--color-primary-highlight-foreground)',
    boxShadow: '0 0 10px rgba(var(--color-status-danger) 0.55)',
  };
  const inactiveDanger: CSSProperties = {
    ...baseButtonStyles,
    backgroundColor: 'var(--color-danger-button)',
    borderColor: 'var(--color-status-danger)',
    color: 'var(--color-primary-shadow)',
    filter: 'brightness(0.65)',
  };

  const enableStyles = isOff ? inactiveSuccess : activeSuccess;
  const disableStyles = isOff ? activeDanger : inactiveDanger;

  const commonButtonProps = {
    className: 'toggle-button px-3 d-flex align-items-center justify-content-center fw-semibold',
    disabled: !enabled,
  };

  return (
    <ButtonGroup className="toggle-button-group shadow-sm rounded overflow-hidden border-0 bg-dark d-flex w-auto">
      <Button
        {...commonButtonProps}
        style={disableStyles}
        onClick={() => enabled && !isOff && onClick()}
        variant="light"
      >
        <TbLetterX size={18} />
      </Button>
      <Button
        {...commonButtonProps}
        style={enableStyles}
        onClick={() => enabled && isOff && onClick()}
        variant="light"
      >
        <TbCheck size={18} />
      </Button>
    </ButtonGroup>
  );
};

ToggleButton.displayName = 'ToggleButton';

export default ToggleButton;
